import React from 'react';
import { Tag } from 'antd';
import moment from 'moment';

/**
 * ArchivedForms için veri işleme yardımcı fonksiyonları
 */

/**
 * Form içeriğini parse et
 * @param {string|object} formContent - Parse edilecek form içeriği
 * @returns {object} Parse edilmiş form içeriği
 */
export const parseFormContent = (formContent) => {
  try {
    if (typeof formContent === 'string') {
      return JSON.parse(formContent);
    }
    return formContent || {};
  } catch (error) {
    console.error('Form içeriği parse edilirken hata:', error);
    return {};
  }
};

/**
 * Formları filtrele
 * @param {Array} forms - Filtrelenecek formlar
 * @param {string} searchText - Arama metni
 * @returns {Array} Filtrelenmiş formlar
 */
export const filterForms = (forms, searchText) => {
  if (!searchText) return forms;
  
  const searchLower = searchText.toLowerCase();
  return forms.filter(form => {
    const content = parseFormContent(form.formContent);
    return (
      form.title?.toLowerCase().includes(searchLower) ||
      form.formId?.toLowerCase().includes(searchLower) ||
      (content.description && content.description.toLowerCase().includes(searchLower))
    );
  });
};

/**
 * Form tipini formatla
 * @param {number} fileType - Dosya tipi (0: Form, 1: Tablo)
 * @param {function} t - Çeviri fonksiyonu
 * @returns {React.Element} Format edilmiş dosya tipi
 */
export const formatFileType = (fileType, t) => {
  const config = {
    0: { label: 'form', color: 'blue' },
    1: { label: 'table', color: 'green' }
  }[fileType] || { label: 'unknown', color: 'default' };

  return (
    <Tag color={config.color}>
      {t(config.label)}
    </Tag>
  );
};

/**
 * Tarih formatla
 * @param {string} date - Formatlanacak tarih
 * @param {string} format - Tarih formatı (varsayılan: 'DD.MM.YYYY HH:mm')
 * @returns {string} Formatlanmış tarih
 */
export const formatDate = (date, format = 'DD.MM.YYYY HH:mm') => {
  if (!date) return '-';
  
  try {
    return moment(date).format(format);
  } catch (error) {
    console.error('Tarih formatlanırken hata:', error);
    return '-';
  }
};

/**
 * Form durumunu formatla
 * @param {boolean} isActive - Form aktif mi?
 * @param {function} t - Çeviri fonksiyonu
 * @returns {React.Element} Format edilmiş durum
 */
export const formatStatus = (isActive, t) => {
  return (
    <Tag color={isActive ? 'success' : 'error'}>
      {t(isActive ? 'active' : 'passive')}
    </Tag>
  );
};

/**
 * Tablo kolonlarını oluştur
 * @param {object} options - Tablo konfigürasyon seçenekleri
 * @returns {function} Kolonları döndüren fonksiyon
 */
export const createTableColumns = ({ 
  onRestore, 
  onDelete, 
  onPreview,
  ActionButtonsComponent 
}) => {
  return (t) => [
    {
      title: t('form_id'),
      dataIndex: 'formId',
      key: 'formId',
      width: 100,
      sorter: (a, b) => a.formId.localeCompare(b.formId),
    },
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: t('file_type'),
      dataIndex: 'fileType',
      key: 'fileType',
      width: 120,
      render: (fileType) => formatFileType(fileType, t),
      filters: [
        { text: t('form'), value: 0 },
        { text: t('table'), value: 1 },
      ],
      onFilter: (value, record) => record.fileType === value,
    },
    {
      title: t('description'),
      dataIndex: 'formContent',
      key: 'formContent',
      width: 300,
      render: (formContent) => {
        const content = parseFormContent(formContent);
        return content.description || '-';
      },
    },
    {
      title: t('created_date'),
      dataIndex: 'createDate',
      key: 'createDate',
      width: 180,
      render: (date) => formatDate(date),
      sorter: (a, b) => moment(a.createDate).unix() - moment(b.createDate).unix(),
    },
    {
      title: t('updated_date'),
      dataIndex: 'editDate',
      key: 'editDate',
      width: 180,
      render: (date) => formatDate(date),
      sorter: (a, b) => {
        if (!a.editDate) return -1;
        if (!b.editDate) return 1;
        return moment(a.editDate).unix() - moment(b.editDate).unix();
      },
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => formatStatus(isActive, t),
      filters: [
        { text: t('active'), value: true },
        { text: t('passive'), value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <ActionButtonsComponent
          record={record}
          onRestore={onRestore}
          onDelete={onDelete}
          onPreview={onPreview}
        />
      ),
    },
  ];
};
